import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { CameraModeId, type CameraMode } from '../CameraMode';

/**
 * Free-look camera for reviewing scenes during development. This is
 * deliberately not "BehindBallCamera" or any real gameplay camera — those
 * need an actual ball/hole to target, which don't exist yet. This mode
 * exists purely so CameraManager has something real to manage today.
 *
 * FOV is tuned narrower than a typical free-look default (48° vs three's
 * usual 50-75° examples) for a more compressed, telephoto "broadcast lens"
 * feel — real golf broadcast cameras are usually long lenses, not wide
 * establishing shots. OrbitControls' own damping provides the smoothing;
 * a true cinematic camera *mode* (behind-ball, follow) is still future
 * work — see camera/CameraDamping.ts for the smoothing primitive it'll use.
 */
export class DevOrbitCameraMode implements CameraMode {
  readonly id = CameraModeId.DevOrbit;
  readonly camera: THREE.PerspectiveCamera;
  private readonly controls: OrbitControls;

  constructor(
    domElement: HTMLElement,
    target = new THREE.Vector3(0, 1, -15),
    position = new THREE.Vector3(0, 6, 12),
  ) {
    this.camera = new THREE.PerspectiveCamera(48, 1, 0.1, 2000);
    this.camera.position.copy(position);

    this.controls = new OrbitControls(this.camera, domElement);
    this.controls.target.copy(target);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.08;
    this.controls.enabled = false; // becomes true on activate()
  }

  activate(): void {
    this.controls.enabled = true;
  }

  deactivate(): void {
    this.controls.enabled = false;
  }

  update(): void {
    this.controls.update();
  }

  handleResize(aspect: number): void {
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }

  dispose(): void {
    this.controls.dispose();
  }
}
